import React, { useState } from "react";
import { useVendors } from "@/hooks/use-vendors";
import { Helmet } from "react-helmet";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreateVendorDialog } from "@/components/expenses/create-vendor-dialog";
import { EditVendorDialog } from "@/components/expenses/edit-vendor-dialog";
import { Loader2, Plus, PencilIcon, MoreHorizontal } from "lucide-react";
import { Layout } from "@/components/layout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function VendorsPage() {
  const { currentBusiness } = useCurrentBusiness();
  const [isCreateVendorOpen, setIsCreateVendorOpen] = useState(false);
  const [isEditVendorOpen, setIsEditVendorOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  const { data: vendors = [], isLoading } = useVendors(currentBusiness?.id || 0);

  const handleEditVendor = (vendor: any) => {
    setSelectedVendor(vendor);
    setIsEditVendorOpen(true);
  };
  
  if (!currentBusiness) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <h2 className="text-2xl font-semibold mb-2">No Business Selected</h2>
          <p className="text-muted-foreground">Please select a business to manage vendors.</p>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Helmet>
        <title>Vendors | ZentroBooks</title>
        <meta name="description" content="Manage your vendors in ZentroBooks - keep track of your suppliers, contractors, and service providers." />
      </Helmet>
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vendors</h1>
          <p className="text-muted-foreground">
            Manage your suppliers, contractors, and service providers
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateVendorOpen(true)} 
          className="flex items-center gap-1"
        >
          <Plus className="h-4 w-4" />
          New Vendor
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Vendor List</CardTitle>
          <CardDescription>
            View and manage all your vendors in one place
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center my-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No vendors found</p>
              <Button 
                variant="outline" 
                onClick={() => setIsCreateVendorOpen(true)}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add your first vendor
              </Button>
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Payment Terms</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.name}</TableCell>
                      <TableCell>
                        {vendor.email && (
                          <div>{vendor.email}</div>
                        )}
                        {vendor.phone && (
                          <div className="text-muted-foreground text-sm">{vendor.phone}</div>
                        )}
                      </TableCell>
                      <TableCell>{vendor.paymentTerms || "-"}</TableCell>
                      <TableCell>{vendor.category || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (vendor.is_active || vendor.isActive)
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {(vendor.is_active || vendor.isActive) ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditVendor(vendor)}>
                              <PencilIcon className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      <CreateVendorDialog 
        isOpen={isCreateVendorOpen} 
        onClose={() => setIsCreateVendorOpen(false)} 
        businessId={currentBusiness.id} 
      />
      
      <EditVendorDialog
        isOpen={isEditVendorOpen}
        onClose={() => setIsEditVendorOpen(false)}
        vendor={selectedVendor}
      />
    </Layout>
  );
}